import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect } from "react";

export const useEnsurePartnerLink = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  // Check if current user needs partner linking
  const checkPartnerLinkQuery = useQuery({
    queryKey: ["partner-link-check", session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id || !session?.user?.email) {
        console.log("🔍 No session for partner link check");
        return null;
      }

      console.log("🔍 Checking partner link for:", session.user.email);

      // Check if there's a partner record with this email but no user_id or wrong user_id
      const { data: partner, error } = await supabase
        .from('crm_partners')
        .select('id, email, user_id, portal_access_enabled')
        .eq('email', session.user.email)
        .maybeSingle();

      if (error) {
        console.error("❌ Error checking partner:", error);
        return null;
      }

      console.log("👤 Partner record found:", partner);

      // Return linking info if partner exists but user_id is null or mismatched
      if (partner && (!partner.user_id || partner.user_id !== session.user.id)) {
        return {
          needsLinking: true,
          partnerEmail: partner.email,
          partnerId: partner.id
        };
      }

      return {
        needsLinking: false,
        partnerEmail: partner?.email,
        partnerId: partner?.id
      };
    },
    enabled: !!session?.user?.id && !!session?.user?.email,
    retry: 1
  });

  // Mutation to setup partner access
  const setupPartnerAccessMutation = useMutation({
    mutationFn: async (partnerEmail: string) => {
      console.log("🔗 Setting up partner access for:", partnerEmail);
      
      const { data, error } = await supabase.rpc('setup_partner_user_access', {
        partner_email: partnerEmail,
        auth_user_id: session?.user?.id
      });

      if (error) {
        console.error("❌ Error setting up partner access:", error);
        throw error;
      }

      console.log("✅ Partner access setup successful:", data);
      
      // Update user's profile to mark them as a partner and sync basic info
      try {
        const { data: partnerData } = await supabase
          .from('crm_partners')
          .select('id, first_name, last_name, email, phone')
          .eq('email', partnerEmail)
          .single();
          
        if (partnerData) {
          const profileUpdates: any = {
            contact_type: 'Partner',
            external_owner_id: partnerData.id
          };

          // Sync name fields if available
          if (partnerData.first_name) {
            profileUpdates.first_name = partnerData.first_name;
          }
          if (partnerData.last_name) {
            profileUpdates.last_name = partnerData.last_name;
          }
          if (partnerData.email) {
            profileUpdates.email = partnerData.email;
          }
          if (partnerData.phone) {
            profileUpdates.phone = partnerData.phone;
          }

          await supabase
            .from('profiles')
            .update(profileUpdates)
            .eq('user_id', session?.user?.id);
            
          console.log("✅ Updated user profile with partner tagging and basic info:", profileUpdates);
        }
      } catch (profileError) {
        console.warn("Could not update profile with partner info:", profileError);
      }
      
      return data;
    },
    onSuccess: () => {
      console.log("🔄 Invalidating queries after partner link");
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: ["partner-link-check"] });
      queryClient.invalidateQueries({ queryKey: ["crm-access"] });
      queryClient.invalidateQueries({ queryKey: ["partner-profile"] });
    }
  });

  // Auto-link if needed
  useEffect(() => {
    const linkData = checkPartnerLinkQuery.data;
    
    if (linkData?.needsLinking && 
        linkData.partnerEmail && 
        !setupPartnerAccessMutation.isPending &&
        !setupPartnerAccessMutation.isSuccess) {
      
      console.log("🚀 Auto-linking partner:", linkData.partnerEmail);
      setupPartnerAccessMutation.mutate(linkData.partnerEmail);
    }
  }, [checkPartnerLinkQuery.data, setupPartnerAccessMutation]);

  return {
    isCheckingLink: checkPartnerLinkQuery.isLoading,
    isLinking: setupPartnerAccessMutation.isPending,
    linkError: setupPartnerAccessMutation.error,
    needsLinking: checkPartnerLinkQuery.data?.needsLinking || false,
    isLinked: checkPartnerLinkQuery.data?.needsLinking === false,
    partnerEmail: checkPartnerLinkQuery.data?.partnerEmail
  };
};